import type { Color, MapViewState, PickingInfo } from "@deck.gl/core";
import { ColumnLayer } from "@deck.gl/layers";
import { DeckGL } from "@deck.gl/react";
import { type ReactNode, useMemo } from "react";
import { Map as MapLibreMap } from "react-map-gl/maplibre";

import type { GeoHexCell } from "@busright/data-contract";

import { type RampName, rampColor } from "@/lib/color-ramp";
import type { HeightScale } from "@/lib/scale";
import { scaleNorm } from "@/lib/scale";

import "maplibre-gl/dist/maplibre-gl.css";

// Free CARTO basemaps — no API key required. Dark is the default; the light/high-contrast option
// (Richard's "map too dark to read" feedback) swaps to the Positron light style. Dark stays default.
export type BasemapStyle = "dark" | "light";

const BASEMAP_STYLES: Record<BasemapStyle, string> = {
  dark: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
  light: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
};

// CONUS-centered initial camera with a pitch so the 3D columns read immediately.
const INITIAL_VIEW_STATE: MapViewState = {
  longitude: -97,
  latitude: 39,
  zoom: 3.6,
  pitch: 45,
  bearing: 0,
};

// Cell side is 0.05deg; render columns slightly narrower than the cell so neighbors don't merge.
const CELL_RADIUS_M = 2200;

export interface GeoMapTooltip {
  readonly cell: GeoHexCell;
}

export interface GeoMapProps {
  readonly cells: readonly GeoHexCell[];
  // Metric value range used for both color and height normalization.
  readonly minValue: number;
  readonly maxValue: number;
  readonly heightScale: HeightScale;
  readonly peakHeightM: number;
  readonly ramp: RampName;
  readonly unit: string;
  readonly reducedMotion: boolean;
  // Basemap style — dark (default) or light/high-contrast for readability.
  readonly basemap?: BasemapStyle;
}

const cellValue = (cell: GeoHexCell): number => (cell.value === null ? 0 : cell.value);

export const GeoMap = ({
  cells,
  minValue,
  maxValue,
  heightScale,
  peakHeightM,
  ramp,
  unit,
  reducedMotion,
  basemap = "dark",
}: GeoMapProps): ReactNode => {
  const layers = useMemo(() => {
    const range = maxValue - minValue;
    const norm = (v: number): number => (range > 0 ? (v - minValue) / range : 0);
    return [
      new ColumnLayer<GeoHexCell>({
        id: "geo-hex-columns",
        data: cells as GeoHexCell[],
        diskResolution: 6,
        radius: CELL_RADIUS_M,
        extruded: true,
        pickable: true,
        elevationScale: 1,
        getPosition: (d: GeoHexCell) => [d.lng, d.lat],
        getElevation: (d: GeoHexCell) => scaleNorm(norm(cellValue(d)), heightScale) * peakHeightM,
        getFillColor: (d: GeoHexCell): Color => {
          const [r, g, b] = rampColor(ramp, scaleNorm(norm(cellValue(d)), heightScale));
          return [r, g, b, 220];
        },
        updateTriggers: {
          getElevation: [minValue, maxValue, heightScale, peakHeightM],
          getFillColor: [minValue, maxValue, heightScale, ramp],
        },
        material: {
          ambient: 0.6,
          diffuse: 0.6,
          shininess: 32,
          specularColor: [40, 50, 70],
        },
      }),
    ];
  }, [cells, minValue, maxValue, heightScale, peakHeightM, ramp]);

  const getTooltip = (info: PickingInfo<GeoHexCell>): string | null => {
    const cell = info.object;
    if (!cell) {
      return null;
    }
    const valueLabel = cell.value === null ? "—" : `${cell.value.toLocaleString()} ${unit}`.trim();
    return [
      `Value: ${valueLabel}`,
      `Pings: ${cell.count.toLocaleString()}`,
      `Lat/Lng: ${cell.lat.toFixed(2)}, ${cell.lng.toFixed(2)}`,
    ].join("\n");
  };

  return (
    <DeckGL
      initialViewState={INITIAL_VIEW_STATE}
      controller={{ dragRotate: !reducedMotion }}
      layers={layers}
      getTooltip={getTooltip}
      style={{ position: "absolute", inset: "0" }}
    >
      <MapLibreMap reuseMaps mapStyle={BASEMAP_STYLES[basemap]} attributionControl={false} />
    </DeckGL>
  );
};
